import {
  ACCESSORY_STATUS_LABELS,
  ASSET_STATUS_LABELS,
  hasPermission,
  REQUEST_STATUS_LABELS,
  REQUEST_TYPE_LABELS,
} from "@edm/shared";
import { useQuery } from "@tanstack/react-query";
import { Download, FileBarChart, RefreshCw, Wallet } from "lucide-react";
import { EmptyState } from "@/components/empty-state";
import { PageHeader } from "@/components/page-header";
import { StatCard } from "@/components/stat-card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { apiFetch } from "@/lib/api";
import { useMe } from "@/lib/auth";
import type { ReportSummary } from "@/lib/master-data";

const CATEGORY_LABELS: Record<string, string> = {
  asset: "资产",
  accessory: "低值易耗",
  consumable: "耗材",
  software: "软件",
};

interface DepreciationReport {
  settings: { method: "none" | "straight_line"; residualRatePercent: number };
  summary: { count: number; originalCents: number; accumulatedCents: number; netCents: number };
  items: {
    assetCode: string;
    materialName: string;
    status: string;
    inboundDate: string;
    originalCents: number;
    accumulatedCents: number;
    netCents: number;
  }[];
}

interface AssetLifecycle {
  total: number;
  buckets: { key: string; label: string; count: number }[];
  dueSoon: {
    assetCode: string;
    materialName: string;
    inboundDate: string;
    expectedReplaceDate: string;
    daysLeft: number;
  }[];
  dueSoonTotal: number;
  byCategory: { category: string; count: number; avgAgeDays: number }[];
}

function money(cents: number): string {
  return `¥${(cents / 100).toLocaleString("zh-CN", { minimumFractionDigits: 2 })}`;
}

export function ReportsPage() {
  const query = useQuery({
    queryKey: ["report-summary"],
    queryFn: () => apiFetch<ReportSummary>("/api/v1/reports/summary"),
  });
  const data = query.data;
  const { data: me } = useMe();
  const canManageFinance = Boolean(me && hasPermission(me.permissions, "admin:access:manage"));
  const depreciation = useQuery({
    queryKey: ["report-depreciation"],
    queryFn: () => apiFetch<DepreciationReport>("/api/v1/reports/depreciation"),
  });
  const lifecycle = useQuery({
    queryKey: ["report-asset-lifecycle"],
    queryFn: () => apiFetch<AssetLifecycle>("/api/v1/reports/asset-lifecycle"),
  });

  function exportCsv() {
    if (!data) return;
    const rows = [
      ["指标", "值"],
      ["资产总量", String(data.assets.total)],
      ["资产原值（元）", (data.assets.totalValueCents / 100).toFixed(2)],
      ["低值易耗总量", String(data.accessories.total)],
      ["申请总量", String(data.requests.total)],
      ["软件许可", String(data.licenses.total)],
      ["有效许可", String(data.licenses.active)],
      ["90 天内到期许可", String(data.licenses.expiring90Days)],
      ["耗材库存总量", String(data.consumables.quantity)],
      ["低库存耗材种类", String(data.consumables.lowStock)],
      ...data.assets.byStatus.map((row) => [
        `资产状态：${ASSET_STATUS_LABELS[row.status as keyof typeof ASSET_STATUS_LABELS] ?? row.status}`,
        String(row.count),
      ]),
      ...data.requests.byStatus.map((row) => [
        `申请状态：${REQUEST_STATUS_LABELS[row.status as keyof typeof REQUEST_STATUS_LABELS] ?? row.status}`,
        String(row.count),
      ]),
    ];
    const csv = rows
      .map((row) => row.map((cell) => `"${cell.replaceAll('"', '""')}"`).join(","))
      .join("\n");
    const blob = new Blob([`\uFEFF${csv}`], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `edm-report-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="统计报表"
        description="资产、申请、低值易耗、许可与耗材的汇总视图；金额按资产原值统计，不做折旧重算。"
        actions={
          <>
            <Button variant="outline" onClick={() => query.refetch()} disabled={query.isFetching}>
              <RefreshCw className="h-4 w-4" />
              刷新
            </Button>
            <Button onClick={exportCsv} disabled={!data}>
              <Download className="h-4 w-4" />
              导出 CSV
            </Button>
          </>
        }
      />

      {query.isLoading ? (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          {["s1", "s2", "s3", "s4"].map((key) => (
            <Skeleton key={key} className="h-28 w-full" />
          ))}
        </div>
      ) : !data ? (
        <EmptyState title="暂无报表数据" description="资产入库、申请和许可登记后会产生统计。" />
      ) : (
        <>
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <StatCard
              label="资产总量"
              value={data.assets.total}
              hint={money(data.assets.totalValueCents)}
              icon={FileBarChart}
            />
            <StatCard
              label="申请总量"
              value={data.requests.total}
              hint={`${data.requests.byStatus.length} 种状态`}
              icon={FileBarChart}
              accent="warning"
            />
            <StatCard
              label="有效许可"
              value={data.licenses.active}
              hint={`${data.licenses.allocatedSeats} / ${data.licenses.totalSeats} 席位已分配`}
              icon={Wallet}
              accent="success"
            />
            <StatCard
              label="耗材低库存"
              value={data.consumables.lowStock}
              hint={`${data.consumables.outOfStock} 种缺货`}
              icon={Wallet}
              accent="danger"
            />
          </div>

          <div className="grid gap-6 xl:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle>资产状态</CardTitle>
                <CardDescription>按生命周期状态统计数量与原值。</CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>状态</TableHead>
                      <TableHead>数量</TableHead>
                      <TableHead>原值</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.assets.byStatus.map((row) => (
                      <TableRow key={row.status}>
                        <TableCell>
                          {ASSET_STATUS_LABELS[row.status as keyof typeof ASSET_STATUS_LABELS] ??
                            row.status}
                        </TableCell>
                        <TableCell>{row.count}</TableCell>
                        <TableCell>{money(row.valueCents)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>资产品类</CardTitle>
                <CardDescription>按物料类别汇总资产数量与原值。</CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>类别</TableHead>
                      <TableHead>数量</TableHead>
                      <TableHead>原值</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.assets.byCategory.map((row) => (
                      <TableRow key={row.category}>
                        <TableCell>{CATEGORY_LABELS[row.category] ?? row.category}</TableCell>
                        <TableCell>{row.count}</TableCell>
                        <TableCell>{money(row.valueCents)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>申请状态</CardTitle>
                <CardDescription>从待审批到完成的分布。</CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>状态</TableHead>
                      <TableHead>数量</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.requests.byStatus.map((row) => (
                      <TableRow key={row.status}>
                        <TableCell>
                          {REQUEST_STATUS_LABELS[
                            row.status as keyof typeof REQUEST_STATUS_LABELS
                          ] ?? row.status}
                        </TableCell>
                        <TableCell>{row.count}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>申请类型</CardTitle>
                <CardDescription>资产、低值易耗、耗材、软件席位分布。</CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>类型</TableHead>
                      <TableHead>数量</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.requests.byType.map((row) => (
                      <TableRow key={row.requestType}>
                        <TableCell>
                          {REQUEST_TYPE_LABELS[
                            row.requestType as keyof typeof REQUEST_TYPE_LABELS
                          ] ?? row.requestType}
                        </TableCell>
                        <TableCell>{row.count}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>低值易耗状态</CardTitle>
                <CardDescription>在库、在用、返库与报废。</CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>状态</TableHead>
                      <TableHead>数量</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.accessories.byStatus.map((row) => (
                      <TableRow key={row.status}>
                        <TableCell>
                          {ACCESSORY_STATUS_LABELS[
                            row.status as keyof typeof ACCESSORY_STATUS_LABELS
                          ] ?? row.status}
                        </TableCell>
                        <TableCell>{row.count}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>许可与耗材</CardTitle>
                <CardDescription>许可到期与库存风险。</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3 text-sm">
                <p>
                  许可总数：{data.licenses.total}，有效 {data.licenses.active}，90 天内到期{" "}
                  {data.licenses.expiring90Days}，已过期 {data.licenses.expired}
                </p>
                <p>
                  席位：{data.licenses.allocatedSeats} / {data.licenses.totalSeats} 已分配
                </p>
                <p>
                  耗材：{data.consumables.kinds} 种，库存 {data.consumables.quantity}，低库存{" "}
                  {data.consumables.lowStock}，缺货 {data.consumables.outOfStock}
                </p>
              </CardContent>
            </Card>

            <Card className="xl:col-span-2">
              <CardHeader>
                <CardTitle>资产寿命与更换</CardTitle>
                <CardDescription>
                  按入库日期与物料预计使用期计算；未来 90 天内到期的资产共{" "}
                  {lifecycle.data?.dueSoonTotal ?? 0} 台。
                </CardDescription>
              </CardHeader>
              <CardContent className="grid gap-6 xl:grid-cols-[0.8fr_1.2fr]">
                <div className="space-y-2">
                  {(lifecycle.data?.buckets ?? []).map((bucket) => (
                    <div
                      key={bucket.key}
                      className="flex items-center justify-between rounded-md border border-border px-3 py-2 text-sm"
                    >
                      <span>{bucket.label}</span>
                      <Badge variant="secondary">{bucket.count}</Badge>
                    </div>
                  ))}
                  <div className="pt-2 text-xs text-muted-foreground">
                    {lifecycle.data?.byCategory
                      .map(
                        (row) =>
                          `${CATEGORY_LABELS[row.category] ?? row.category} 均龄 ${row.avgAgeDays} 天`,
                      )
                      .join(" · ")}
                  </div>
                </div>
                <div>
                  {(lifecycle.data?.dueSoon ?? []).length === 0 ? (
                    <p className="text-sm text-muted-foreground">未来 90 天没有到期资产。</p>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>资产编号</TableHead>
                          <TableHead>物料</TableHead>
                          <TableHead>预计更换</TableHead>
                          <TableHead>剩余</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {(lifecycle.data?.dueSoon ?? []).map((row) => (
                          <TableRow key={row.assetCode}>
                            <TableCell className="font-mono text-xs">{row.assetCode}</TableCell>
                            <TableCell>{row.materialName}</TableCell>
                            <TableCell>{row.expectedReplaceDate}</TableCell>
                            <TableCell>
                              <Badge variant={row.daysLeft < 0 ? "danger" : "warning"}>
                                {row.daysLeft < 0
                                  ? `逾期 ${Math.abs(row.daysLeft)} 天`
                                  : `${row.daysLeft} 天`}
                              </Badge>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card className="xl:col-span-2">
              <CardHeader>
                <CardTitle>折旧与净值</CardTitle>
                <CardDescription>
                  当前方法：{depreciation.data?.settings.method === "none" ? "不折旧" : "直线法"}，
                  残值率 {depreciation.data?.settings.residualRatePercent ?? 5}%。
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid gap-3 sm:grid-cols-3">
                  <div className="rounded-md border border-border p-3">
                    <p className="text-xs text-muted-foreground">原值</p>
                    <p className="text-lg font-semibold">
                      {money(depreciation.data?.summary.originalCents ?? 0)}
                    </p>
                  </div>
                  <div className="rounded-md border border-border p-3">
                    <p className="text-xs text-muted-foreground">累计折旧</p>
                    <p className="text-lg font-semibold">
                      {money(depreciation.data?.summary.accumulatedCents ?? 0)}
                    </p>
                  </div>
                  <div className="rounded-md border border-border p-3">
                    <p className="text-xs text-muted-foreground">净值</p>
                    <p className="text-lg font-semibold">
                      {money(depreciation.data?.summary.netCents ?? 0)}
                    </p>
                  </div>
                </div>
                {(depreciation.data?.items ?? []).length === 0 ? (
                  <p className="text-sm text-muted-foreground">暂无资产折旧数据。</p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>资产</TableHead>
                        <TableHead>物料</TableHead>
                        <TableHead>原值</TableHead>
                        <TableHead>累计折旧</TableHead>
                        <TableHead>净值</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(depreciation.data?.items ?? []).slice(0, 20).map((item) => (
                        <TableRow key={item.assetCode}>
                          <TableCell className="font-mono text-xs">{item.assetCode}</TableCell>
                          <TableCell>{item.materialName}</TableCell>
                          <TableCell>{money(item.originalCents)}</TableCell>
                          <TableCell>{money(item.accumulatedCents)}</TableCell>
                          <TableCell>{money(item.netCents)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
                {canManageFinance && depreciation.data ? (
                  <div className="flex flex-wrap items-end gap-3 rounded-md border border-border p-3">
                    <div className="space-y-1">
                      <p className="text-xs text-muted-foreground">折旧方法</p>
                      <select
                        className="h-9 rounded-md border border-input bg-background px-3 text-sm"
                        value={depreciation.data.settings.method}
                        onChange={async (event) => {
                          await apiFetch("/api/v1/admin/settings/depreciation", {
                            method: "PUT",
                            body: JSON.stringify({
                              ...depreciation.data?.settings,
                              method: event.target.value,
                            }),
                          });
                          await depreciation.refetch();
                        }}
                      >
                        <option value="straight_line">直线法</option>
                        <option value="none">不折旧</option>
                      </select>
                    </div>
                    <div className="space-y-1">
                      <p className="text-xs text-muted-foreground">残值率（%）</p>
                      <Input
                        type="number"
                        className="w-28"
                        defaultValue={depreciation.data.settings.residualRatePercent}
                        onBlur={async (event) => {
                          await apiFetch("/api/v1/admin/settings/depreciation", {
                            method: "PUT",
                            body: JSON.stringify({
                              ...depreciation.data?.settings,
                              residualRatePercent: Number(event.target.value),
                            }),
                          });
                          await depreciation.refetch();
                        }}
                      />
                    </div>
                  </div>
                ) : null}
              </CardContent>
            </Card>
          </div>
        </>
      )}
    </div>
  );
}
